import React, { useState, useEffect } from 'react';
import { Table, Typography, Spin, Alert, Card, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { fetchPOLineItemPreview } from '../api';
import { formatCurrency } from '../utils/stringUtils';

const { Text, Title } = Typography;

const POLineItemBreakdown = ({ poId, country, constants }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    const loadPreview = async () => {
      if (!poId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await fetchPOLineItemPreview(poId);
        setPreviewData(data);
        if (data.error) {
          setError(data.error);
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load line item preview');
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, [poId]);

  if (loading) {
    return (
      <Card size="small" style={{ marginTop: 16 }}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin tip="Loading line item breakdown..." />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card size="small" style={{ marginTop: 16 }}>
        <Alert
          message="Line Item Breakdown Unavailable"
          description={error}
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  if (!previewData || !previewData.line_items || previewData.line_items.length === 0) {
    if (previewData?.message) {
      return (
        <Card size="small" style={{ marginTop: 16 }}>
          <Text type="secondary">{previewData.message}</Text>
        </Card>
      );
    }
    return null;
  }

  const columns = [
    {
      title: 'Site',
      dataIndex: 'site_name',
      key: 'site_name',
      width: '40%',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>{text}</span>
          {record.used_default_area && (
            <Tooltip title="Using default area of 1 acre (4,047 m²) - site area not set">
              <InfoCircleOutlined style={{ color: '#d48806', fontSize: '14px' }} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Area (m²)',
      dataIndex: 'area_m2',
      key: 'area_m2',
      width: '20%',
      align: 'right',
      render: (value, record) => (
        <span style={{ color: record.used_default_area ? '#d48806' : undefined }}>
          {value?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: '%',
      dataIndex: 'percentage',
      key: 'percentage',
      width: '15%',
      align: 'right',
      render: (value) => `${value?.toFixed(2)}%`,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: '25%',
      align: 'right',
      render: (value) => formatCurrency(value, country, constants),
    },
  ];

  const dataSource = previewData.line_items.map((item, index) => ({
    key: item.site_id || index,
    ...item,
  }));

  return (
    <Card 
      size="small" 
      style={{ marginTop: 16 }}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0 }}>Site-Level Line Item Breakdown</Title>
          {previewData.route_name && (
            <Text type="secondary">Route: {previewData.route_name}</Text>
          )}
        </div>
      }
    >
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        bordered
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
              <Table.Summary.Cell index={0}>
                <Text strong>Total ({previewData.line_items.length} sites)</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Text strong>
                  {previewData.line_items.reduce((sum, item) => sum + (item.area_m2 || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <Text strong>100%</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <Text strong>{formatCurrency(previewData.total_amount, country, constants)}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      {!previewData.is_balanced && (
        <Alert
          message="Rounding Notice"
          description={`Sum of line items (${formatCurrency(previewData.sum_allocated, country, constants)}) differs slightly from total (${formatCurrency(previewData.total_amount, country, constants)}) due to rounding.`}
          type="info"
          showIcon
          style={{ marginTop: 8 }}
        />
      )}
    </Card>
  );
};

export default POLineItemBreakdown;

